// React
import React, { useState, useContext, useEffect } from 'react';
import nextId from 'react-id-generator';

// Material UI
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@material-ui/core';
import LanguageIcon from '@material-ui/icons/Language';
import { makeStyles } from '@material-ui/core/styles';

// Contexts
import { DetailContext } from '../../../contexts/detail';

// Shared
import eventBus from '../../../shared/eventBus';
import {
  dialog as dialogStyles,
  dialogTitle as dialogTitleStyles,
  dialogContent as dialogContentStyles,
  dialogActions as dialogActionsStyles
} from '../../../shared/theme';

// Components
import FormInput from '../FormInput/FormInput';

// Hooks
import useMounted from '../../../hooks/useMounted';

// Custom styles
const useDialogStyles = makeStyles(dialogStyles);
const useDialogTitleStyles = makeStyles(dialogTitleStyles);
const useDialogContentStyles = makeStyles(dialogContentStyles);
const useDialogActionsStyles = makeStyles(dialogActionsStyles);

export default function LocalizedFormInput({
  title,
  value,
  setValue,
  disabled = false
}) {
  // Detail Context
  const { cultures } = useContext(DetailContext);

  // The form ID (random)
  const formId = nextId();

  // State that manage the modal opened/closed
  const [open, setOpen] = useState(false);

  // Event to open the modal
  const handleOpen = () => {
    setOpen(true);
  };

  // Event to close the modal
  const handleClose = () => {
    setOpen(false);
  };

  // Event to handle the form submit
  const handleSubmit = (event) => {
    if (event.target.id !== formId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    const formData = new FormData(form);

    let newValue = {
      ...value,
      translations
    };

    for (let [cultureCode, text] of formData.entries()) {
      newValue.translations = newValue.translations.map((translation) =>
        translation.cultureCode === cultureCode
          ? { ...translation, text: text }
          : translation
      );
    }

    setValue(newValue);
    setOpen(false);
  };

  // List of fields/translations
  const [translations, setTranslations] = useState([]);

  // If the component is mounted
  const mounted = useMounted();

  // Listen to possible call from suggested value button
  useEffect(() => {
    eventBus.on('suggestedValueButtonClick', (data_) => {
      if (mounted.current) {
        setOpen(true);

        setTranslations(
          cultures.map((culture) => {
            return {
              ...culture,
              text: data_.suggestedValue
            };
          })
        );
      }
    }); 
    
  }, [cultures, mounted]);

  useEffect(() => {
  
      eventBus.on('dataVersionRestored_init', (data_) => {
        if(mounted.current) {
          // set the translations and value in the input
          setTranslations(data_.versionData);
          setValue(data_.versionData);
          // trigger the event
          // to close the modal and show
          // the snackbar message to the user
          eventBus.dispatch('dataVersionRestored');
        }    
      });

  }, [mounted, setValue]);
  

  // Set value on load
  useEffect(() => {
    setTranslations(
      cultures &&
        cultures.map((culture) => {
          let text = '';

          if (value && value.translations) {
            const translation = value.translations.find(
              (item) => item.cultureCode === culture.cultureCode
            );

            if (translation && translation.text) {
              text = translation.text;
            }
          }

          return {
            ...culture,
            text: text
          };
        })
    );
  }, [cultures, value]);

  // Custom styles
  const dialogClasses = useDialogStyles();
  const dialogTitleClasses = useDialogTitleStyles();
  const dialogContentClasses = useDialogContentStyles();
  const dialogActionsClasses = useDialogActionsStyles();

  return (
    <>
      <Button
        color="primary"
        startIcon={<LanguageIcon fontSize="small" />}
        onClick={handleOpen}
        disabled={disabled}
        id={`edit-${title.toLowerCase().replace(/[^a-z]/i, '')}`}>
        Edit
      </Button>
      <Dialog
        classes={dialogClasses}
        open={open}
        onClose={handleClose}
        maxWidth="md"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title" classes={dialogTitleClasses}>
          <Typography variant="subtitle1" component="div">
            Edit Language
          </Typography>
          <Typography variant="h2" component="div">
            {title}
          </Typography>
        </DialogTitle>
        <DialogContent classes={dialogContentClasses}>
          {translations.length > 0 && (
            <form id={formId} onSubmit={handleSubmit}>
              <Grid container spacing={4}>
                {translations &&
                  translations.map((translation, i) => {
                    return (
                      <Grid key={i} item xs={12}>
                        <FormInput
                          type="text"
                          parameter={translation.cultureCode}
                          label={translation.cultureLong}
                          required={translation.defaultCulture}
                          inputValue={translation.text}
                        />
                      </Grid>
                    );
                  })}
              </Grid>
            </form>
          )}
        </DialogContent>
        <DialogActions classes={dialogActionsClasses}>
          <Button
            onClick={handleClose}
            disableElevation={true}
            color="secondary"
            variant="outlined"
            size="large">
            cancel
          </Button>
          <Button
            disableElevation={true}
            color="secondary"
            variant="contained"
            size="large"
            type="submit"
            form={formId}
            id={`edit-${title.toLowerCase().replace(/[^a-z]/i, '')}-button`}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
